import { useState, useEffect } from 'react';
import Title from '../../components/Title';
import CustomModal from '../../components/CustomModal';
import CustomTable from '../../components/CustomTable/CustomTable';
import CustomInput from '../../components/CustomInput';
import { useDispatch, useSelector } from 'react-redux';
import {
    getUsers,
    createUser,
    usersLoadingStatus,
    allUsers,
    editUser,
    deleteUser,
} from '../../redux/slices/usersSlice';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { Space } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

export default function UsersPage() {
    const dispatch = useDispatch();

    const users = useSelector(allUsers);
    const usersStatus = useSelector(usersLoadingStatus);

    const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
    const [isEditUserModalVisible, setIsEditUserModalVisible] = useState(false);
    const [isDeleteUserModalVisible, setIsDeleteUserModalVisible] =
        useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [userId, setUserId] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getUsers());
    }, [dispatch]);

    const toggleAddUserModal = () => {
        setUserEmail('');
        setErrors({});
        setIsAddUserModalVisible(!isAddUserModalVisible);
    };

    const toggleEditUserModal = (record) => {
        if (record === undefined) {
            setIsEditUserModalVisible(!isEditUserModalVisible);
        } else {
            setUserEmail(record.email);
            setUserId(record.id);
            setErrors({});
            setIsEditUserModalVisible(!isEditUserModalVisible);
        }
    };

    const toggleDeleteUserModal = (record) => {
        if (record === undefined) {
            setIsDeleteUserModalVisible(!isDeleteUserModalVisible);
        } else {
            setUserEmail(record.email);
            setUserId(record.id);
            setIsDeleteUserModalVisible(!isDeleteUserModalVisible);
        }
    };

    const onInputChange = (e) => {
        setUserEmail(e.target.value);
    };

    const onAddUser = async () => {
        try {
            await dispatch(createUser({ email: userEmail })).unwrap();
            toast.success('New user successfully added');
            toggleAddUserModal();
            dispatch(getUsers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditUser = async () => {
        const data = { id: userId, body: { email: userEmail } };
        try {
            await dispatch(editUser(data)).unwrap();
            toast.success('Changes saved successfully');
            dispatch(getUsers());
            toggleEditUserModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteUser = async () => {
        try {
            await dispatch(deleteUser(userId)).unwrap();
            toast.success('User deleted successfully');
            dispatch(getUsers());
            toggleDeleteUserModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredUsers = (data, filter) => {
        const formatedData = data?.map((el) => {
            const newEl = { ...el };

            if (newEl.created_at) {
                newEl.created_at = moment(el.created_at).format(
                    'YYYY-MM-DD HH:mm',
                );
            }

            return newEl;
        });
        const filteredData = formatedData.filter((el) =>
            el?.email.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => toggleEditUserModal(record, e)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteUserModal(record, e)}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>Console Defentry - Users</title>
            </Helmet>
            <Title>Users</Title>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['ID', 'Email', 'Added at', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    columnsDataMap={{
                        'Added at': 'created_at',
                    }}
                    onAddButtonClick={toggleAddUserModal}
                    tablePannelTitle="Table of users"
                    topPannelButtonText="Add user"
                    searchInputPlaceholder="Search by email"
                    data={users}
                    getFilteredData={getFilteredUsers}
                    isLoading={usersStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Add new user"
                subText="Please enter the email"
                subTextAlign="left"
                isModalVisible={isAddUserModalVisible}
                onAcceptButtonClick={onAddUser}
                onCancelButtonClick={toggleAddUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Add"
            >
                <CustomInput
                    placeholder="Enter your email"
                    onInputChange={onInputChange}
                    inputValue={userEmail}
                    labelText="Email"
                    width="100%"
                    errorText={errors?.email}
                />
            </CustomModal>
            <CustomModal
                title="Edit user"
                subText="Edit email below"
                subTextAlign="left"
                isModalVisible={isEditUserModalVisible}
                onAcceptButtonClick={onEditUser}
                onCancelButtonClick={toggleEditUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Save"
            >
                <CustomInput
                    placeholder="Enter your email"
                    onInputChange={onInputChange}
                    inputValue={userEmail}
                    labelText="Email"
                    width="100%"
                    errorText={errors?.email}
                />
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${userEmail} `}</span>
                        user?
                    </p>
                }
                subText="You will delete this user without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteUserModalVisible}
                onAcceptButtonClick={onDeleteUser}
                onCancelButtonClick={toggleDeleteUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            ></CustomModal>
        </div>
    );
}
